import React, { useState, useMemo } from 'react';
import { Folder, Search, X } from 'lucide-react';

export default function AllProjectsModal({
  isOpen,
  onClose,
  projectsList = [],
  totalHours = 0,
  title = "All Projects"
}) {
  const [searchQuery, setSearchQuery] = useState("");

  const filteredList = useMemo(() => {
    if (!projectsList) return [];
    if (!searchQuery.trim()) return projectsList;
    return projectsList.filter(p =>
      p.name?.toLowerCase().includes(searchQuery.trim().toLowerCase())
    );
  }, [projectsList, searchQuery]);

  if (!isOpen) return null;

  const displayTotalHours = typeof totalHours === 'number'
    ? totalHours.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 1 })
    : totalHours;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/50 backdrop-blur-sm p-3 sm:p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg sm:max-w-xl max-h-[85vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-200 border border-slate-200">
        {/* Header */}
        <div className="flex items-start justify-between p-4 sm:p-5 border-b border-slate-100 relative bg-white">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center shrink-0 shadow-sm">
              <Folder className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h3 className="text-base sm:text-lg font-bold text-slate-800">{title}</h3>
              <p className="text-[11px] sm:text-[12px] font-medium text-slate-500 mt-0.5">
                {projectsList.length} Projects • {displayTotalHours} Total Hours
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 hover:bg-slate-100 p-1.5 rounded-lg transition-colors absolute top-4 sm:top-5 right-4 sm:right-5"
          >
            <X size={18} />
          </button>
        </div>

        {/* Search Toolbar */}
        <div className="px-4 sm:px-5 py-3 border-b border-slate-100 flex flex-col sm:flex-row gap-3 items-center justify-between bg-slate-50/40">
          <div className="relative w-full">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-slate-400">
              <Search size={14} />
            </div>
            <input
              type="text"
              className="block w-full pl-9 pr-8 py-2 border border-slate-200 rounded-xl text-[12px] sm:text-[13px] font-medium placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all bg-white"
              placeholder="Search projects..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-2.5 flex items-center text-slate-400 hover:text-slate-600"
              >
                <X size={14} />
              </button>
            )}
          </div>
        </div>

        {/* List Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-2.5 bg-slate-50 border-b border-slate-100 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
          <span>Project</span>
          <span className="text-right pr-1">Logged Hours</span>
        </div>

        {/* List Body */}
        <div className="flex-1 overflow-y-auto p-0 bg-white divide-y divide-slate-100 custom-scrollbar">
          {filteredList.length > 0 ? (
            filteredList.map((p, idx) => (
              <div
                key={idx}
                className="flex items-center justify-between px-4 sm:px-6 py-3 hover:bg-slate-50/80 transition-colors group"
              >
                <div className="flex items-center gap-2.5 sm:gap-3 min-w-0 pr-4 flex-1">
                  <div className="w-6 h-6 sm:w-7 sm:h-7 rounded-lg bg-blue-50 text-blue-600 font-semibold text-[11px] sm:text-xs flex items-center justify-center shrink-0 border border-blue-100/50">
                    {idx + 1}
                  </div>
                  <span className="text-[12px] sm:text-[13px] font-semibold text-slate-800 truncate" title={p.name}>
                    {p.name}
                  </span>
                </div>
                <div className="flex items-baseline gap-1 shrink-0 pl-2">
                  <span className="text-[13px] sm:text-sm font-bold text-slate-900">
                    {Number(p.hours).toFixed(1)}
                  </span>
                  <span className="text-[11px] sm:text-xs font-semibold text-slate-400">hrs</span>
                </div>
              </div>
            ))
          ) : (
            <div className="px-5 py-12 text-center flex flex-col items-center justify-center text-slate-400">
              <Search className="w-8 h-8 mb-2 text-slate-300" />
              <p className="text-[13px] font-medium text-slate-500">No projects found matching "{searchQuery}"</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
